import React, { useState, useMemo } from 'react';
import '../styles/PatternPanel.css';

/**
 * PatternPanel — Mixer / Peeling Chain Detection
 *
 * Reads graph_data already computed by GraphBuilder.build_graph_data():
 *   graphData.stats.peel_chains        — count of detected peel chains
 *   graphData.stats.peel_chain_nodes   — total addresses in peel chains
 *   graphData.stats.circular_flows     — count of circular flow cycles
 *   graphData.stats.hubs               — top-N hub addresses (betweenness)
 *   graphData.nodes[*].peel_chain      — boolean flag on each node
 *   graphData.nodes[*].betweenness     — betweenness centrality score
 *   graphData.nodes[*].risk_score      — overall risk score
 *
 * Props:
 *   graphData          — full graph data object from /investigations/:id/graph
 *   hiddenNodes        — Set of currently hidden node ids (passed down from InvestigationDetail)
 *   hiddenEdges        — Set of currently hidden edge ids
 *   onViewStateChange  — (newHiddenEdges, newHiddenNodes) => void — same callback used by Graph.js
 *   onJumpToGraph      — () => void — switches parent to the Graph tab
 */
export default function PatternPanel({ graphData, hiddenNodes, hiddenEdges, onViewStateChange, onJumpToGraph }) {
  const [expandedSection, setExpandedSection] = useState('peel');
  const [highlightActive, setHighlightActive] = useState(null); // 'peel' | 'hubs' | null

  const stats  = graphData?.stats  || {};
  const nodes  = graphData?.nodes  || [];
  const edges  = graphData?.edges  || [];

  // ── Derived sets ──────────────────────────────────────────────────
  const peelNodes = useMemo(() => nodes.filter(n => n.peel_chain), [nodes]);
  const hubNodes  = useMemo(() => (stats.hubs || []).map(h => ({
    ...h,
    risk_score: nodes.find(n => n.id === h.address)?.risk_score ?? 0,
  })), [stats.hubs, nodes]);

  // High-risk nodes not already in the hub list (for the risk section)
  const highRiskNodes = useMemo(() =>
    nodes.filter(n => n.risk_score >= 7 && !hubNodes.find(h => h.address === n.id))
         .sort((a, b) => b.risk_score - a.risk_score)
         .slice(0, 20),
  [nodes, hubNodes]);

  // ── Highlight helpers ─────────────────────────────────────────────

  // Dim everything EXCEPT the given address set + their connecting edges
  const highlightAddresses = (addrSet) => {
    if (!onViewStateChange) return;
    const keepNodes = new Set(addrSet);
    // Keep edges that connect two highlighted nodes
    const keepEdges = new Set(
      edges
        .filter(e => keepNodes.has(e.from) && keepNodes.has(e.to))
        .map(e => e.id)
    );
    // Hide all nodes not in the set, hide all edges not in the set
    const newHiddenNodes = new Set(nodes.map(n => n.id).filter(id => !keepNodes.has(id)));
    const newHiddenEdges = new Set(edges.map(e => e.id).filter(id => !keepEdges.has(id)));
    onViewStateChange(newHiddenEdges, newHiddenNodes);
  };

  const handleHighlightPeel = () => {
    if (highlightActive === 'peel') {
      // Restore
      onViewStateChange(new Set(), new Set());
      setHighlightActive(null);
    } else {
      highlightAddresses(peelNodes.map(n => n.id));
      setHighlightActive('peel');
      onJumpToGraph?.();
    }
  };

  const handleHighlightHubs = () => {
    if (highlightActive === 'hubs') {
      onViewStateChange(new Set(), new Set());
      setHighlightActive(null);
    } else {
      highlightAddresses(hubNodes.map(h => h.address));
      setHighlightActive('hubs');
      onJumpToGraph?.();
    }
  };

  const handleHighlightHighRisk = () => {
    if (highlightActive === 'risk') {
      onViewStateChange(new Set(), new Set());
      setHighlightActive(null);
    } else {
      const riskAddrs = [...hubNodes.map(h => h.address), ...highRiskNodes.map(n => n.id)];
      highlightAddresses(riskAddrs);
      setHighlightActive('risk');
      onJumpToGraph?.();
    }
  };

  const handleRestoreAll = () => {
    onViewStateChange(new Set(), new Set());
    setHighlightActive(null);
  };

  // ── Render helpers ────────────────────────────────────────────────
  const riskLabel = (score) => {
    if (score >= 7.5) return { label: 'CRITICAL', cls: 'risk-critical' };
    if (score >= 5)   return { label: 'HIGH',     cls: 'risk-high'     };
    if (score >= 2.5) return { label: 'MEDIUM',   cls: 'risk-medium'   };
    return               { label: 'LOW',      cls: 'risk-low'      };
  };

  const addrShort = (addr) => addr ? `${addr.slice(0, 10)}…${addr.slice(-6)}` : '—';

  const SectionHeader = ({ id, label, badge, badgeCls }) => (
    <button
      className={`pp-section-hdr${expandedSection === id ? ' active' : ''}`}
      onClick={() => setExpandedSection(expandedSection === id ? null : id)}
    >
      <span>{label}</span>
      {badge !== undefined && (
        <span className={`pp-badge ${badgeCls || ''}`}>{badge}</span>
      )}
      <span className="pp-chevron">{expandedSection === id ? '▲' : '▼'}</span>
    </button>
  );

  if (!graphData || nodes.length === 0) {
    return (
      <div className="pattern-panel">
        <div className="pp-empty">
          <p>No graph data yet.</p>
          <p className="pp-hint">Fetch blockchain data or import transactions to run pattern analysis.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pattern-panel">
      <div className="pp-header">
        <h3>🔍 Pattern Detection</h3>
        <p className="pp-subtitle">
          Structural analysis of the fund-flow graph — peel chains, circular flows, hub wallets, and risk scoring.
        </p>
      </div>

      {/* ── Summary strip ── */}
      <div className="pp-summary-strip">
        <div className="pp-stat">
          <div className="pp-stat-value">{nodes.length}</div>
          <div className="pp-stat-label">Addresses</div>
        </div>
        <div className="pp-stat">
          <div className="pp-stat-value">{edges.length}</div>
          <div className="pp-stat-label">Transactions</div>
        </div>
        <div className={`pp-stat ${(stats.peel_chains || 0) > 0 ? 'pp-stat-warn' : ''}`}>
          <div className="pp-stat-value">{stats.peel_chains || 0}</div>
          <div className="pp-stat-label">Peel Chains</div>
        </div>
        <div className={`pp-stat ${(stats.circular_flows || 0) > 0 ? 'pp-stat-warn' : ''}`}>
          <div className="pp-stat-value">{stats.circular_flows || 0}</div>
          <div className="pp-stat-label">Circular Flows</div>
        </div>
        <div className={`pp-stat ${(stats.high_risk_count || 0) > 0 ? 'pp-stat-danger' : ''}`}>
          <div className="pp-stat-value">{stats.high_risk_count || 0}</div>
          <div className="pp-stat-label">High-Risk Addrs</div>
        </div>
      </div>

      {highlightActive && (
        <div className="pp-highlight-bar">
          <span>🔦 Graph filtered to highlighted pattern</span>
          <button className="pp-restore-btn" onClick={handleRestoreAll}>✕ Restore All</button>
        </div>
      )}

      {/* ── Peel Chains ── */}
      <SectionHeader
        id="peel"
        label="⛓ Peel / Pass-Through Chains"
        badge={peelNodes.length}
        badgeCls={peelNodes.length > 0 ? 'badge-warn' : ''}
      />
      {expandedSection === 'peel' && (
        <div className="pp-section-body">
          <p className="pp-explainer">
            A <strong>peel chain</strong> is a sequence of wallets each with exactly one inbound and one
            outbound transaction — classic sequential pass-through to obscure the origin or destination of funds.
            {peelNodes.length === 0
              ? ' No peel chains were detected in the current graph.'
              : ` ${peelNodes.length} address${peelNodes.length !== 1 ? 'es' : ''} across ${stats.peel_chains} chain${stats.peel_chains !== 1 ? 's' : ''} detected.`}
          </p>

          {peelNodes.length > 0 && (
            <>
              <button
                className={`pp-action-btn${highlightActive === 'peel' ? ' active' : ''}`}
                onClick={handleHighlightPeel}
                title="Dim everything else on the graph and jump to the Graph tab"
              >
                {highlightActive === 'peel' ? '✕ Clear highlight' : '🔦 Highlight on graph'}
              </button>

              <div className="pp-addr-list">
                {peelNodes.map(n => {
                  const { label: rlabel, cls } = riskLabel(n.risk_score || 0);
                  return (
                    <div key={n.id} className="pp-addr-row">
                      <code className="pp-addr-code" title={n.id}>{addrShort(n.id)}</code>
                      <span className={`pp-risk-badge ${cls}`}>{rlabel}</span>
                      <span className="pp-addr-meta">
                        {n.tx_count || 0} TX · in {n.in_degree || 0} / out {n.out_degree || 0}
                      </span>
                      <button
                        className="pp-copy-btn"
                        onClick={() => navigator.clipboard.writeText(n.id)}
                        title="Copy address"
                      >📋</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Circular Flows ── */}
      <SectionHeader
        id="circular"
        label="🔄 Circular Flows"
        badge={stats.circular_flows || 0}
        badgeCls={(stats.circular_flows || 0) > 0 ? 'badge-warn' : ''}
      />
      {expandedSection === 'circular' && (
        <div className="pp-section-body">
          <p className="pp-explainer">
            <strong>Circular flows</strong> occur when value returns to an address earlier in its own
            transaction chain — cycles of length ≥ 3. This pattern is associated with wash trading,
            layering, or attempts to obscure the ultimate destination of funds.
          </p>
          {(stats.circular_flows || 0) === 0 ? (
            <p className="pp-none">No circular flows detected in the current graph.</p>
          ) : (
            <div className="pp-info-box pp-info-warn">
              <strong>{stats.circular_flows}</strong> circular flow cycle{stats.circular_flows !== 1 ? 's' : ''} detected.
              To inspect specific cycles, use the <strong>Hop Tracer</strong> tab with a focus address from the peel chain
              or hub list above — bidirectional mode will trace the loops.
            </div>
          )}
        </div>
      )}

      {/* ── Hub Wallets ── */}
      <SectionHeader
        id="hubs"
        label="🌐 Hub / Relay Wallets"
        badge={hubNodes.length}
        badgeCls={hubNodes.length > 0 ? 'badge-warn' : ''}
      />
      {expandedSection === 'hubs' && (
        <div className="pp-section-body">
          <p className="pp-explainer">
            <strong>Hub wallets</strong> sit on a disproportionate share of the shortest paths between
            other addresses (high betweenness centrality). They are likely relay, cash-out, or
            exchange points — structurally critical to the fund flow.
          </p>

          {hubNodes.length === 0 ? (
            <p className="pp-none">No significant hub wallets detected.</p>
          ) : (
            <>
              <button
                className={`pp-action-btn${highlightActive === 'hubs' ? ' active' : ''}`}
                onClick={handleHighlightHubs}
              >
                {highlightActive === 'hubs' ? '✕ Clear highlight' : '🔦 Highlight on graph'}
              </button>

              <div className="pp-hub-list">
                {hubNodes.map((h, i) => {
                  const { label: rlabel, cls } = riskLabel(h.risk_score || 0);
                  return (
                    <div key={h.address} className="pp-hub-row">
                      <span className="pp-hub-rank">#{i + 1}</span>
                      <code className="pp-addr-code" title={h.address}>{addrShort(h.address)}</code>
                      <span className={`pp-risk-badge ${cls}`}>{rlabel}</span>
                      <span className="pp-addr-meta">
                        centrality {(h.betweenness || 0).toFixed(3)} · pagerank {(h.pagerank || 0).toFixed(4)}
                      </span>
                      <button
                        className="pp-copy-btn"
                        onClick={() => navigator.clipboard.writeText(h.address)}
                        title="Copy address"
                      >📋</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Risk Scores ── */}
      <SectionHeader
        id="risk"
        label="⚠ High-Risk Addresses"
        badge={stats.high_risk_count || 0}
        badgeCls={(stats.high_risk_count || 0) > 0 ? 'badge-danger' : ''}
      />
      {expandedSection === 'risk' && (
        <div className="pp-section-body">
          <p className="pp-explainer">
            Addresses with a risk score ≥ 7.0 / 10, ranked by score. Score is computed from graph
            structure — transaction frequency, in/out degree, and betweenness centrality. A high score
            is an investigative prioritisation signal, not a determination of wrongdoing.
          </p>

          {(stats.high_risk_count || 0) === 0 && highRiskNodes.length === 0 ? (
            <p className="pp-none">No high-risk addresses detected.</p>
          ) : (
            <>
              <button
                className={`pp-action-btn${highlightActive === 'risk' ? ' active' : ''}`}
                onClick={handleHighlightHighRisk}
              >
                {highlightActive === 'risk' ? '✕ Clear highlight' : '🔦 Highlight on graph'}
              </button>

              <div className="pp-addr-list">
                {[...hubNodes, ...highRiskNodes].map(n => {
                  const addr  = n.address || n.id;
                  const score = n.risk_score || 0;
                  const { label: rlabel, cls } = riskLabel(score);
                  return (
                    <div key={addr} className="pp-addr-row">
                      <code className="pp-addr-code" title={addr}>{addrShort(addr)}</code>
                      <span className={`pp-risk-badge ${cls}`}>{rlabel}</span>
                      <span className="pp-addr-meta">score {score.toFixed(2)}</span>
                      <button
                        className="pp-copy-btn"
                        onClick={() => navigator.clipboard.writeText(addr)}
                        title="Copy address"
                      >📋</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Interpretation Guide ── */}
      <SectionHeader id="guide" label="📖 Interpretation Guide" />
      {expandedSection === 'guide' && (
        <div className="pp-section-body pp-guide">
          <div className="pp-guide-item">
            <strong>⛓ Peel Chain</strong>
            <p>Sequential wallets each receiving and forwarding value once. Minimum 2 nodes in sequence. Common in structured layering schemes.</p>
          </div>
          <div className="pp-guide-item">
            <strong>🔄 Circular Flow</strong>
            <p>Value returns to a previously-seen wallet. Cycles ≥ 3 hops. Indicates wash trading or an attempt to return funds to the origin.</p>
          </div>
          <div className="pp-guide-item">
            <strong>🌐 Hub Wallet</strong>
            <p>High betweenness centrality — sits on many shortest paths. Likely a relay or cash-out wallet. Warrant manual review and cross-referencing with Known Address Registry.</p>
          </div>
          <div className="pp-guide-item">
            <strong>⚠ Risk Score</strong>
            <p>0–10 scale. 7.5+ = CRITICAL. Computed from degree, tx count, and centrality. Not a definitive finding — use as a triage signal to focus manual investigation.</p>
          </div>
          <div className="pp-guide-item">
            <strong>🔦 Highlight on graph</strong>
            <p>Dims all addresses and transactions not part of the selected pattern. Use "Restore All" or the Graph toolbar restore buttons to reset. Highlight state is not persisted.</p>
          </div>
        </div>
      )}
    </div>
  );
}
